import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PARTNER_TIERS } from '@/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const partnerSchema = z.object({
  company_name: z.string().min(2, 'Company name required'),
  contact_name: z.string().min(2, 'Contact name required'),
  email: z.string().email('Invalid email'),
  phone: z.string().optional(),
  budget_range: z.string().min(1, 'Please select a budget range'),
  goals: z.string().min(20, 'Please provide more details')
});

export default function Partners() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue } = useForm({
    resolver: zodResolver(partnerSchema)
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/partners`, data);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      alert('Error submitting inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white" data-testid="partners-page">
      <section className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-unbounded font-black mb-8"
            data-testid="partners-title"
          >
            Partner With Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl font-manrope max-w-4xl"
          >
            Support the next generation of AI builders and get early access to top talent.
          </motion.p>
        </div>
      </section>

      <section className="py-24" data-testid="sponsor-tiers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-16">Sponsorship Tiers</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {PARTNER_TIERS.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border-2 border-black/10 p-8 hover:border-accent transition-colors"
                data-testid={`tier-${tier.name.toLowerCase()}`}
              >
                <h3 className="text-3xl font-unbounded font-black mb-4">{tier.name}</h3>
                <p className="text-4xl font-unbounded font-black text-accent mb-8">{tier.investment}</p>
                <div className="space-y-3">
                  {tier.deliverables.map((item) => (
                    <div key={item} className="flex items-start gap-2">
                      <CheckCircle className="text-accent flex-shrink-0 mt-1" size={16} />
                      <p className="font-manrope text-gray-700">{item}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50" data-testid="partner-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
              data-testid="partner-confirmation"
            >
              <CheckCircle className="text-accent mx-auto mb-6" size={64} />
              <h2 className="text-4xl font-unbounded font-black mb-6">Inquiry Submitted!</h2>
              <p className="text-lg font-manrope text-gray-700">
                Thank you for your interest. We'll be in touch within 48 hours.
              </p>
            </motion.div>
          ) : (
            <div>
              <h2 className="text-4xl font-unbounded font-black mb-8">Get in Touch</h2>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input id="company_name" {...register('company_name')} className="mt-2" data-testid="company-name-input" />
                  {errors.company_name && <p className="text-red-600 text-sm mt-1">{errors.company_name.message}</p>}
                </div>

                <div>
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input id="contact_name" {...register('contact_name')} className="mt-2" data-testid="contact-name-input" />
                  {errors.contact_name && <p className="text-red-600 text-sm mt-1">{errors.contact_name.message}</p>}
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" {...register('email')} className="mt-2" data-testid="email-input" />
                  {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <Label htmlFor="phone">Phone (Optional)</Label>
                  <Input id="phone" {...register('phone')} className="mt-2" data-testid="phone-input" />
                </div>

                <div>
                  <Label htmlFor="budget_range">Budget Range *</Label>
                  <Select onValueChange={(value) => setValue('budget_range', value)}>
                    <SelectTrigger className="mt-2" data-testid="budget-range-select">
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="$5,000-$10,000">$5,000-$10,000</SelectItem>
                      <SelectItem value="$10,000-$25,000">$10,000-$25,000</SelectItem>
                      <SelectItem value="$25,000-$50,000">$25,000-$50,000</SelectItem>
                      <SelectItem value="$50,000-$100,000">$50,000-$100,000</SelectItem>
                      <SelectItem value="$100,000+">$100,000+</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.budget_range && <p className="text-red-600 text-sm mt-1">{errors.budget_range.message}</p>}
                </div>

                <div>
                  <Label htmlFor="goals">Partnership Goals *</Label>
                  <Textarea id="goals" {...register('goals')} rows={4} className="mt-2" data-testid="goals-textarea" />
                  {errors.goals && <p className="text-red-600 text-sm mt-1">{errors.goals.message}</p>}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-accent text-white px-8 py-6 font-bold uppercase hover:bg-accent-hover"
                  data-testid="submit-partner-inquiry-button"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </Button>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
